import type {
  JsonDiffLine,
  TextDiffSegment,
  ViewChangeEntry,
  ViewChangesIdNameMaps,
} from "@/types/view-changes";
import {
  formatIntegrationDisplayName,
  getIntegrationReferenceLabel,
} from "@/utils/integration-status";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeForCompare(value: unknown): unknown {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(normalizeForCompare);
  }
  if (isPlainObject(value)) {
    const entries = Object.keys(value)
      .sort()
      .map((key) => [key, normalizeForCompare(value[key])] as const);
    return Object.fromEntries(entries);
  }
  return value;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  return (
    JSON.stringify(normalizeForCompare(a)) ===
    JSON.stringify(normalizeForCompare(b))
  );
}

export function prettifyFieldKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value.trim() === "" ? "—" : value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (value.every((item) => typeof item === "string" || typeof item === "number")) {
      return value.join(", ");
    }
    return JSON.stringify(value, null, 2);
  }
  if (isPlainObject(value)) {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function leafFieldKey(key: string): string {
  const withoutIndexes = key.replace(/\[\d+\]/g, "");
  const parts = withoutIndexes.split(".").filter(Boolean);
  return parts[parts.length - 1] ?? key;
}

export function resolveValueWithNames(
  value: unknown,
  fieldKey: string,
  idNameMaps?: ViewChangesIdNameMaps
): unknown {
  if (!idNameMaps) return value;
  const map =
    idNameMaps[fieldKey] ?? idNameMaps[leafFieldKey(fieldKey)];
  if (!map) return value;

  if (typeof value === "string") {
    return map[value] ?? value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === "string") return map[item] ?? item;
      return item;
    });
  }
  return value;
}

export function resolveEntriesWithNames(
  entries: ViewChangeEntry[],
  idNameMaps?: ViewChangesIdNameMaps
): ViewChangeEntry[] {
  if (!idNameMaps || Object.keys(idNameMaps).length === 0) return entries;
  return entries.map((entry) => ({
    ...entry,
    before: resolveValueWithNames(entry.before, entry.key, idNameMaps),
    after: resolveValueWithNames(entry.after, entry.key, idNameMaps),
  }));
}

export function toPrettyJson(value: unknown): string {
  try {
    return JSON.stringify(normalizeForCompare(value), null, 2) ?? "null";
  } catch {
    return String(value);
  }
}

/**
 * Pretty JSON for diffs: expand multiline strings onto separate lines so
 * a one-word prompt edit does not dump the entire string as one red/green block.
 */
export function toPrettyJsonForDiff(value: unknown): string {
  try {
    const raw = JSON.stringify(normalizeForCompare(value), null, 2) ?? "null";
    // Turn "line1\nline2" into real line breaks inside the quoted string body
    // so line-level diff can highlight only the edited line.
    return raw.replace(/\\n/g, "\n");
  } catch {
    return String(value);
  }
}

/**
 * Compact before/after JSON with only changed variables.
 * Long strings are shortened to an excerpt around the edit.
 */
export function buildChangedVariablesJson(
  entries: ViewChangeEntry[]
): { before: Record<string, unknown>; after: Record<string, unknown> } {
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};

  for (const entry of entries) {
    const key = entry.key;
    if (
      typeof entry.before === "string" &&
      typeof entry.after === "string" &&
      (entry.before.length > 160 || entry.after.length > 160)
    ) {
      const excerpt = excerptAroundChanges(entry.before, entry.after);
      before[key] = excerpt.before;
      after[key] = excerpt.after;
    } else {
      before[key] = entry.before ?? null;
      after[key] = entry.after ?? null;
    }
  }

  return { before, after };
}

function excerptAroundChanges(
  beforeText: string,
  afterText: string,
  radius = 48
): { before: string; after: string } {
  const segments = buildInlineTextDiff(beforeText, afterText);
  let beforeOut = "";
  let afterOut = "";
  let sawChange = false;

  for (const segment of segments) {
    if (segment.kind === "same") {
      const text = segment.text;
      if (!sawChange) {
        const slice =
          text.length > radius ? `…${text.slice(-radius)}` : text;
        beforeOut += slice;
        afterOut += slice;
      } else {
        const slice =
          text.length > radius ? `${text.slice(0, radius)}…` : text;
        beforeOut += slice;
        afterOut += slice;
        if (text.length > radius) break;
      }
      continue;
    }

    sawChange = true;
    if (segment.kind === "removed") {
      beforeOut += segment.text;
    } else {
      afterOut += segment.text;
    }
  }

  if (!sawChange) {
    return {
      before: flattenExcerpt(
        beforeText.length > radius * 2
          ? `${beforeText.slice(0, radius)}…${beforeText.slice(-radius)}`
          : beforeText
      ),
      after: flattenExcerpt(
        afterText.length > radius * 2
          ? `${afterText.slice(0, radius)}…${afterText.slice(-radius)}`
          : afterText
      ),
    };
  }

  return {
    before: flattenExcerpt(beforeOut),
    after: flattenExcerpt(afterOut),
  };
}

function flattenExcerpt(text: string): string {
  return text.replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ").trim();
}

/** Split into words and whitespace/punctuation so inline highlights stay readable. */
function tokenizeForInlineDiff(text: string): string[] {
  return text.match(/\s+|[A-Za-z0-9_]+|[^\sA-Za-z0-9_]+/g) ?? [text];
}

function tokenizeForCharDiff(text: string): string[] {
  return text.split("");
}

function buildTokenDiff(
  beforeTokens: string[],
  afterTokens: string[]
): TextDiffSegment[] {
  const n = beforeTokens.length;
  const m = afterTokens.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    Array(m + 1).fill(0)
  );

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      if (beforeTokens[i] === afterTokens[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const segments: TextDiffSegment[] = [];
  let i = 0;
  let j = 0;

  const push = (kind: TextDiffSegment["kind"], text: string) => {
    if (!text) return;
    const last = segments[segments.length - 1];
    if (last && last.kind === kind) {
      last.text += text;
      return;
    }
    segments.push({ kind, text });
  };

  while (i < n && j < m) {
    if (beforeTokens[i] === afterTokens[j]) {
      push("same", beforeTokens[i]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push("removed", beforeTokens[i]);
      i += 1;
    } else {
      push("added", afterTokens[j]);
      j += 1;
    }
  }

  while (i < n) {
    push("removed", beforeTokens[i]);
    i += 1;
  }
  while (j < m) {
    push("added", afterTokens[j]);
    j += 1;
  }

  return segments;
}

/**
 * Vapi-style inline text diff: highlight only the added/removed characters or words.
 * Uses character-level for normal field lengths; word-level for very long prompts.
 */
export function buildInlineTextDiff(
  beforeText: string,
  afterText: string
): TextDiffSegment[] {
  if (beforeText === afterText) {
    return [{ kind: "same", text: afterText }];
  }

  const useChars =
    beforeText.length <= 4000 && afterText.length <= 4000;

  return buildTokenDiff(
    useChars
      ? tokenizeForCharDiff(beforeText)
      : tokenizeForInlineDiff(beforeText),
    useChars ? tokenizeForCharDiff(afterText) : tokenizeForInlineDiff(afterText)
  );
}

export function getChangedEntries(
  before: unknown,
  after: unknown,
  labels?: Record<string, string>,
  pathPrefix = ""
): ViewChangeEntry[] {
  const beforeObj = isPlainObject(before) ? before : null;
  const afterObj = isPlainObject(after) ? after : null;

  if (!beforeObj && !afterObj) {
    if (valuesEqual(before, after)) return [];
    return [
      {
        key: pathPrefix || "value",
        label: labels?.[pathPrefix || "value"] ?? prettifyFieldKey(pathPrefix || "value"),
        before,
        after,
      },
    ];
  }

  const keys = new Set([
    ...Object.keys(beforeObj ?? {}),
    ...Object.keys(afterObj ?? {}),
  ]);

  const entries: ViewChangeEntry[] = [];

  for (const key of Array.from(keys).sort()) {
    const fullKey = pathPrefix ? `${pathPrefix}.${key}` : key;
    const beforeValue = beforeObj?.[key];
    const afterValue = afterObj?.[key];

    if (valuesEqual(beforeValue, afterValue)) continue;

    const bothObjects =
      isPlainObject(beforeValue) && isPlainObject(afterValue);
    const bothArrays = Array.isArray(beforeValue) && Array.isArray(afterValue);

    if (bothObjects) {
      entries.push(
        ...getChangedEntries(beforeValue, afterValue, labels, fullKey)
      );
      continue;
    }

    if (
      bothArrays &&
      (beforeValue.some(isPlainObject) || afterValue.some(isPlainObject))
    ) {
      const maxLen = Math.max(beforeValue.length, afterValue.length);
      for (let index = 0; index < maxLen; index += 1) {
        const itemBefore = beforeValue[index];
        const itemAfter = afterValue[index];
        if (valuesEqual(itemBefore, itemAfter)) continue;
        const itemKey = `${fullKey}[${index}]`;
        if (isPlainObject(itemBefore) || isPlainObject(itemAfter)) {
          entries.push(
            ...getChangedEntries(
              itemBefore ?? {},
              itemAfter ?? {},
              labels,
              itemKey
            )
          );
        } else {
          entries.push({
            key: itemKey,
            label:
              labels?.[itemKey] ??
              labels?.[fullKey] ??
              `${prettifyFieldKey(fullKey)} ${index + 1}`,
            before: itemBefore,
            after: itemAfter,
          });
        }
      }
      continue;
    }

    entries.push({
      key: fullKey,
      label:
        labels?.[fullKey] ??
        labels?.[key] ??
        prettifyFieldKey(fullKey.includes(".") ? key : fullKey),
      before: beforeValue,
      after: afterValue,
    });
  }

  return entries;
}

/**
 * Myers-inspired line diff via LCS for unified JSON view.
 * Collapses long string replacements into inline highlights and only keeps
 * changed regions (+ a little context) so huge prompts are not dumped wholesale.
 */
export function buildUnifiedJsonDiff(
  beforeText: string,
  afterText: string,
  contextLines = 2
): JsonDiffLine[] {
  const beforeLines = beforeText.split("\n");
  const afterLines = afterText.split("\n");
  const n = beforeLines.length;
  const m = afterLines.length;

  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    Array(m + 1).fill(0)
  );

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      if (beforeLines[i] === afterLines[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const raw: JsonDiffLine[] = [];
  let i = 0;
  let j = 0;
  let beforeLine = 1;
  let afterLine = 1;

  while (i < n && j < m) {
    if (beforeLines[i] === afterLines[j]) {
      raw.push({
        kind: "same",
        text: beforeLines[i],
        beforeLine,
        afterLine,
      });
      i += 1;
      j += 1;
      beforeLine += 1;
      afterLine += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      raw.push({
        kind: "removed",
        text: beforeLines[i],
        beforeLine,
      });
      i += 1;
      beforeLine += 1;
    } else {
      raw.push({
        kind: "added",
        text: afterLines[j],
        afterLine,
      });
      j += 1;
      afterLine += 1;
    }
  }

  while (i < n) {
    raw.push({
      kind: "removed",
      text: beforeLines[i],
      beforeLine,
    });
    i += 1;
    beforeLine += 1;
  }

  while (j < m) {
    raw.push({
      kind: "added",
      text: afterLines[j],
      afterLine,
    });
    j += 1;
    afterLine += 1;
  }

  const withInline: JsonDiffLine[] = [];
  for (let idx = 0; idx < raw.length; idx += 1) {
    const current = raw[idx];
    const next = raw[idx + 1];

    const canInline =
      current.kind === "removed" &&
      next?.kind === "added" &&
      current.text.length > 40 &&
      next.text.length > 40 &&
      shareUsefulPrefix(current.text, next.text);

    if (canInline && next) {
      withInline.push({
        kind: "added",
        text: next.text,
        beforeLine: current.beforeLine,
        afterLine: next.afterLine,
        segments: buildInlineTextDiff(current.text, next.text),
      });
      idx += 1;
      continue;
    }

    withInline.push(current);
  }

  return collapseToChangedHunks(withInline, contextLines);
}

function shareUsefulPrefix(a: string, b: string): boolean {
  const limit = Math.min(a.length, b.length, 80);
  let shared = 0;
  while (shared < limit && a[shared] === b[shared]) shared += 1;
  return shared >= 12;
}

function collapseToChangedHunks(
  lines: JsonDiffLine[],
  contextLines: number
): JsonDiffLine[] {
  if (lines.length === 0) return lines;

  const interesting = new Set<number>();
  lines.forEach((line, index) => {
    if (line.kind === "added" || line.kind === "removed") {
      for (
        let offset = -contextLines;
        offset <= contextLines;
        offset += 1
      ) {
        const at = index + offset;
        if (at >= 0 && at < lines.length) interesting.add(at);
      }
    }
  });

  if (interesting.size === 0) {
    return [{ kind: "same", text: "(no differences)" }];
  }

  const sorted = Array.from(interesting).sort((a, b) => a - b);
  const result: JsonDiffLine[] = [];
  let previous = -2;

  for (const index of sorted) {
    if (previous >= 0 && index > previous + 1) {
      result.push({ kind: "context-gap", text: "…" });
    }
    result.push(lines[index]);
    previous = index;
  }

  return result;
}

export const SALES_SETTINGS_FIELD_LABELS: Record<string, string> = {
  title: "Sales Agent Name",
  objective: "Sales Agent Objective Prompt",
  agent_id: "Sales Persona",
  voice_id: "Voice",
  legacy_persona: "Legacy persona",
  legacy_restrictions: "Legacy restrictions",
  call_script: "Call Script",
  messaging_script: "Messaging Script",
  restrictions: "Sales Agent Restrictions",
  voicemail_is_active: "Voicemail Active",
  voicemail_message: "Voicemail Message",
  voicemail_beep_max_await_seconds: "Voicemail Beep Wait",
  inbound_greeting_message: "Inbound Greeting",
  outbound_greeting_message: "Outbound Greeting",
  inbound_first_message_mode: "Inbound Call Opening",
  outbound_first_message_mode: "Outbound Call Opening",
  silence_timeout: "Silence Timeout",
  maximum_duration: "Maximum Duration",
  end_call_message: "End Call Message",
  tasks: "Tasks",
  task_run_answered_call: "Run Tasks on Answered Call",
  session_expiration_seconds: "SMS/Chat Session Timeout",
  analyst_agents: "Analyst Agents",
  transfer_calls: "Transfer Calls",
  transfer_settings: "Transfer Settings",
  dial_keypad: "Dial Keypad",
  enable_end_call_function: "Enable End Call",
  communication_channels: "Communication Channels",
  ai_assist_is_active: "AI Assist",
  ai_assist_action_plan: "AI Assist Action Plan",
  ai_assist_action_plan_restrictions: "AI Assist Restrictions",
  ai_assist_max_actions: "AI Assist Maximum Actions",
  ai_assist_action_interval_minutes: "AI Assist Action Interval",
};

export const SUPPORT_SETTINGS_FIELD_LABELS: Record<string, string> = {
  title: "Support Agent Name",
  objective: "Support Agent Objective Prompt",
  agent_id: "Support Persona",
  voice_id: "Voice",
  legacy_persona: "Legacy persona",
  legacy_restrictions: "Legacy restrictions",
  call_script: "Call Script",
  messaging_script: "Messaging Script",
  restrictions: "Support Agent Restrictions",
  voicemail_is_active: "Voicemail Active",
  voicemail_message: "Voicemail Message",
  voicemail_beep_max_await_seconds: "Voicemail Beep Wait",
  inbound_greeting_message: "Inbound Greeting",
  outbound_greeting_message: "Outbound Greeting",
  inbound_first_message_mode: "Inbound Call Opening",
  outbound_first_message_mode: "Outbound Call Opening",
  silence_timeout: "Silence Timeout",
  maximum_duration: "Maximum Duration",
  end_call_message: "End Call Message",
  tasks: "Tasks",
  task_run_answered_call: "Run Tasks on Answered Call",
  session_expiration_seconds: "SMS/Chat Session Timeout",
  analyst_agents: "Analyst Agents",
  start_date: "Schedule Start Date",
  end_date: "Schedule End Date",
  start_time: "Schedule Start Time",
  end_time: "Schedule End Time",
  days_of_week: "Schedule Active Days",
  concurrent_interactions: "Concurrent Interactions",
  ai_assist_is_active: "AI Assist",
  ai_assist_action_plan: "AI Assist Action Plan",
  ai_assist_action_plan_restrictions: "AI Assist Restrictions",
  ai_assist_max_actions: "AI Assist Maximum Actions",
  ai_assist_action_interval_minutes: "AI Assist Action Interval",
  transfer_calls: "Transfer Calls",
  transfer_settings: "Transfer Settings",
  communication_channels: "Communication Channels",
};

// Command and Analyst own these two maps; they follow the app-wide sentence
// casing standardised in #1103 and are intentionally not title-cased like the
// sales/support maps above.
export const COMMAND_SETTINGS_FIELD_LABELS: Record<string, string> = {
  name: "Agent name",
  description: "Description",
  role: "Role",
  persona: "Persona",
  restrictions: "Restrictions",
  visibility: "Visibility",
  shared_roles: "Shared roles",
  app_integrations: "Connected apps",
  custom_apps: "Custom apps",
  datasources: "Knowledge bases",
  llm_model: "AI model",
  llm_provider: "AI provider",
  llm_temperature: "Temperature",
  llm_max_tokens: "Max tokens",
  llm_timeout: "Timeout",
};

export const ANALYST_SETTINGS_FIELD_LABELS: Record<string, string> = {
  name: "Agent name",
  description: "Description",
  persona: "Persona",
  restrictions: "Restrictions",
  app_integrations: "Connected apps",
  custom_apps: "Custom apps",
  data_table_integrations: "Data tables",
  tasks: "Tasks",
  llm_model: "AI model",
  llm_provider: "AI provider",
  llm_temperature: "Temperature",
  llm_max_tokens: "Max tokens",
  llm_max_completion_tokens: "Max completion tokens",
  llm_timeout: "Timeout",
};

export const CAMPAIGN_FIELD_LABELS: Record<string, string> = {
  campaignEnabled: "Campaign Enabled",
  firstInteractionMode: "First Interaction Mode",
  startDate: "Start Date",
  endDate: "End Date",
  startTime: "Start Time",
  endTime: "End Time",
  timeZone: "Time Zone",
  selectedDayCodes: "Active Days",
  maxRetries: "Max Retries",
  minMinutesBetweenRetries: "Minutes Between Retries",
  voicemailAction: "Voicemail Action",
};

export const CHANNEL_FIELD_LABELS: Record<string, string> = {
  channels: "Channels",
  call_channel: "Voice Calls",
  chat_channel: "Chat",
  type: "Channel Type",
  script: "Script",
  is_primary: "Primary",
  phone_number: "Phone Number",
  phone_number_integration_id: "Phone Number",
};

export const TOOLS_FIELD_LABELS: Record<string, string> = {
  app_integrations: "Connected Apps",
  custom_apps: "Custom Apps",
  datasources: "Knowledge Base / Data Sources",
  data_table_integrations: "Data Tables",
  enable_sms: "Enable SMS Tool",
  dial_keypad: "Dial Keypad",
  enable_end_call_function: "Enable End Call",
  request_start_message: "App Request Start Message",
  request_complete_message: "App Request Complete Message",
  request_failed_message: "App Request Failed Message",
  request_response_delayed_message: "App Request Delayed Message",
  kb_request_start_message: "Knowledge Base Start Message",
  kb_request_complete_message: "Knowledge Base Complete Message",
  kb_request_failed_message: "Knowledge Base Failed Message",
  kb_request_response_delayed_message: "Knowledge Base Delayed Message",
  data_table_request_start_message: "Data Table Start Message",
  data_table_request_complete_message: "Data Table Complete Message",
  data_table_request_failed_message: "Data Table Failed Message",
  data_table_request_response_delayed_message: "Data Table Delayed Message",
};

interface ToolsIdNameMapsInput {
  appIntegrations?: Array<{
    integration_id?: string;
    name?: string;
    alias?: string | null;
    integration_description?: string | null;
    app_description?: string | null;
  }>;
  customApps?: Array<{ custom_app_id?: string; name?: string }>;
  datasources?: Array<{ id: string; name?: string }>;
  integrationCatalog?: Array<{
    id: string;
    alias?: string | null;
    description?: string | null;
    third_party_app?: { name?: string };
  }>;
  customAppCatalog?: Array<{ id: string; name?: string }>;
}

export function buildToolsIdNameMaps(
  input: ToolsIdNameMapsInput
): ViewChangesIdNameMaps {
  const app_integrations: Record<string, string> = {};
  const custom_apps: Record<string, string> = {};
  const datasources: Record<string, string> = {};

  input.appIntegrations?.forEach((item) => {
    if (item.integration_id) {
      const name = item.name || item.integration_id;
      app_integrations[item.integration_id] = formatIntegrationDisplayName(
        name,
        item.alias,
        getIntegrationReferenceLabel(item.integration_description)
      );
    }
  });
  input.customApps?.forEach((item) => {
    if (item.custom_app_id) {
      custom_apps[item.custom_app_id] = item.name || item.custom_app_id;
    }
  });
  input.datasources?.forEach((item) => {
    if (item.id) datasources[item.id] = item.name || item.id;
  });
  input.integrationCatalog?.forEach((item) => {
    if (item.id) {
      const name =
        item.third_party_app?.name || app_integrations[item.id] || item.id;
      app_integrations[item.id] = formatIntegrationDisplayName(
        name,
        item.alias,
        item.description
      );
    }
  });
  input.customAppCatalog?.forEach((item) => {
    if (item.id) {
      custom_apps[item.id] = item.name || custom_apps[item.id] || item.id;
    }
  });

  return { app_integrations, custom_apps, datasources };
}

export function buildTasksIdNameMap(
  tasks?: Array<{ id?: string; title?: string; name?: string }>
): ViewChangesIdNameMaps {
  const map: Record<string, string> = {};
  tasks?.forEach((task) => {
    if (task.id) map[task.id] = task.title || task.name || task.id;
  });
  return { tasks: map };
}

export function buildSettingsIdNameMaps(
  tasks?: Array<{ id?: string; title?: string; name?: string }>,
  voices?: Array<{ voice_id?: string; name?: string }>
): ViewChangesIdNameMaps {
  const taskMap: Record<string, string> = {};
  tasks?.forEach((task) => {
    if (task.id) taskMap[task.id] = task.title || task.name || task.id;
  });
  const voiceMap: Record<string, string> = {};
  voices?.forEach((voice) => {
    if (voice.voice_id) voiceMap[voice.voice_id] = voice.name || voice.voice_id;
  });
  return { tasks: taskMap, voice_id: voiceMap };
}
